import { useState } from "react";
import { api, formatApiErrorDetail, NIVELES, nivelInfo } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

/** Nivel del alumno: el profesor lo asigna la primera vez; después lo solicita con justificación. */
export const NivelAlumnoSelect = ({ alumno, esProfesor, onCambio }) => {
  const [pedir, setPedir] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [enviando, setEnviando] = useState(false);
  const necesitaAprobacion = esProfesor && alumno.nivel_asignado;

  const aplicar = async (nivel, justificacion = "") => {
    setEnviando(true);
    try {
      const { data } = await api.post(`/alumnos/${alumno.id}/nivel`, { nivel, motivo: justificacion });
      toast.success(data.pendiente_aprobacion
        ? "Cambio de nivel solicitado: administración lo revisará"
        : `Nivel actualizado a ${nivelInfo(nivel).label.toLowerCase()}`);
      setPedir(null);
      setMotivo("");
      onCambio?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail), { duration: 9000 });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <>
      <Select value={alumno.nivel || ""}
        onValueChange={(v) => (necesitaAprobacion ? setPedir(v) : aplicar(v))}>
        <SelectTrigger className="h-9 w-[168px] rounded-lg" data-testid={`nivel-select-${alumno.id}`}>
          <SelectValue placeholder="Asignar nivel" />
        </SelectTrigger>
        <SelectContent>
          {NIVELES.map((n) => (
            <SelectItem key={n.key} value={n.key}>{n.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Dialog open={!!pedir} onOpenChange={(v) => !v && setPedir(null)}>
        <DialogContent className="rounded-2xl" data-testid="nivel-solicitud-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Solicitar cambio de nivel</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <p className="text-sm text-muted-foreground">
              {alumno.nombre} pasaría de {nivelInfo(alumno.nivel).label.toLowerCase()} a{" "}
              {nivelInfo(pedir).label.toLowerCase()}. Administración debe aprobarlo.
            </p>
            <Label>Justificación</Label>
            <Textarea value={motivo} data-testid="nivel-motivo-input"
              placeholder="Domina el trazo y la mezcla de color, ya trabaja con autonomía…"
              onChange={(e) => setMotivo(e.target.value)} />
          </div>
          <DialogFooter>
            <Button className="rounded-full font-semibold" data-testid="nivel-solicitar-btn"
              disabled={enviando || motivo.trim().length < 5} onClick={() => aplicar(pedir, motivo)}>
              {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enviar solicitud"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
